import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { ShoppingBag } from "lucide-react";

export default function AdminOrders() {
  const { token } = useAdminAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      const response = await axios.get(`${API}/admin/purchases`, {
        
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setPurchases(response.data);
    } catch (error) {
      console.error("Failed to fetch purchases", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ShoppingBag className="h-8 w-8 text-gold" />
        <h1 className="font-serif text-2xl font-bold">Orders</h1>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : purchases.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-serif text-lg font-semibold mb-2">No orders yet</h3>
            <p className="text-muted-foreground">Orders will appear here once customers make purchases</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {purchases.map((purchase) => (
            <Card key={purchase.purchase_id} className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium">#{purchase.purchase_id}</h3>
                      <Badge className={purchase.payment_status === "paid" ? "bg-green-900/50 text-green-300" : "bg-yellow-900/50 text-yellow-300"}>
                        {purchase.payment_status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{purchase.user_email}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(purchase.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gold">${purchase.total?.toFixed(2)}</p>
                    {purchase.promo_code && (
                      <p className="text-sm text-muted-foreground">
                        Promo: {purchase.promo_code} (-${purchase.promo_discount?.toFixed(2)})
                      </p>
                    )}
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <p className="text-sm font-medium mb-2">Items:</p>
                  <div className="space-y-2">
                    {purchase.items?.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {item.item_type === "bundle" ? "📦" : "🎵"} {item.title}
                        </span>
                        <span>${item.price?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
